import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { BookOpen, ArrowUpRight, Users } from "lucide-react";
import { CardGridSkeleton, FilterPillsSkeleton } from "@/components/skeletons/PageSkeletons";

function pctBar(pct) {
  if (pct >= 90) return "bg-[#059669]";
  if (pct >= 75) return "bg-[#C8102E]";
  if (pct >= 60) return "bg-[#D97706]";
  return "bg-[#E11D48]";
}
function pctColor(pct) {
  if (pct >= 90) return "text-[#059669]";
  if (pct >= 75) return "text-[#C8102E]";
  if (pct >= 60) return "text-[#D97706]";
  return "text-[#E11D48]";
}

export default function Classes() {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [sectionId, setSectionId] = useState("all");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get("/classes").then(r => setClasses(r.data)),
      api.get("/sections").then(r => setSections(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const filtered = sectionId === "all" ? classes : classes.filter(c => c.section_id === sectionId);

  return (
    <div className="min-h-screen">
      <div className="px-6 md:px-12 pt-10 pb-6">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#94A3B8]">Course × Section offerings</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter text-[#0F172A] mt-2" data-testid="classes-heading">Classes.</h1>
        <p className="text-[#475569] mt-3 max-w-2xl">Every class is a course delivered to a specific section. Tap a card to see its roster, timetable slots, and attendance.</p>
      </div>

      <div className="px-6 md:px-12 pb-4">
        {loading ? (
          <FilterPillsSkeleton count={5} />
        ) : (
        <div className="flex items-center gap-2 flex-wrap" data-testid="class-section-filters">
          <button
            onClick={() => setSectionId("all")}
            data-testid="class-filter-all"
            className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ease-in-out ${
              sectionId === "all" ? "bg-[#C8102E] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
            }`}
          >
            All sections
          </button>
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => setSectionId(s.id)}
              data-testid={`class-filter-${s.id}`}
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ease-in-out ${
                sectionId === s.id ? "bg-[#C8102E] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:bg-[#F1F5F9]"
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
        )}
      </div>

      <div className="px-6 md:px-12 pb-16">
        {loading ? (
          <CardGridSkeleton testId="classes-loading" />
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-[#94A3B8]">No classes for this filter.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="classes-grid">
            {filtered.map(c => (
              <button
                key={c.id}
                onClick={() => navigate(`/classes/${c.id}`)}
                className="text-left bg-white border border-[#E2E8F0] rounded-lg p-6 hover:border-[#C8102E] transition-colors duration-200 ease-in-out"
                data-testid={`class-card-${c.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#FDECEF] text-[#C8102E] flex items-center justify-center">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-[#94A3B8]" />
                </div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">{c.course_code}</div>
                <div className="font-heading text-xl font-bold tracking-tight text-[#0F172A] mt-1">{c.course_name}</div>
                <div className="text-sm text-[#475569] mt-1 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Section {c.section_name} · {c.student_count}</div>
                <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-[#94A3B8] font-bold uppercase tracking-[0.15em]">Overall</span>
                    <span className={`font-heading font-bold ${pctColor(c.attendance_pct)}`}>{c.attendance_pct}%</span>
                  </div>
                  <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                    <div className={`h-full ${pctBar(c.attendance_pct)}`} style={{ width: `${c.attendance_pct}%` }} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
